/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Avatar, Button } from '@saleshandy/design-system';
import { Spinner } from 'react-bootstrap';
import { Click, Eye, InfoCircle, Pencil, Send } from '@saleshandy/icons';
import {
  ArrowBackUp,
  ArrowForwardUp,
  Mail,
  Trash,
} from '../../../../../../../../shared/svg';
import CustomReplyForwardEditor from './custom-reply-forward-editor';
import { StateValues } from './custom-reply-forward-editor/types';
import ReplyForwardEditor from '../../../../../../../unified-inbox/sub-components/reply-forward-editor/reply-forward-editor-container';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  formateDateWithTime,
  getIsRequestPending,
  redirectWithoutRefresh,
} from '../../../../../../../../shared/utils';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import { AttachmentType } from '../../../../../../../../shared/editor/types';
import { EmailComposed } from '../../../../../../../../shared/svg/email-composed';
import {
  EmailListFromOpt,
  EmailListTabProps,
  formatArrayWithCount,
  formatDateTime,
  InlineEditorState,
} from './utils/helper';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import {
  ProspectEmailsTabListItem,
  ReplyForwardEmailPreview,
  UnifiedSchedule,
  EmailScheduleStatus,
} from '../../../../../../../unified-inbox/type';
import { UnifiedInboxFeed } from '../../../../../../../unified-inbox/enum/unified-inbox.enum';
import ConfirmationModal from '../../../../../../../../shared/components/confirmation-modal';
import { getReplyForwardEmailEditor } from '../../../../../../../unified-inbox/components/unified-inbox-mails-content/utils/get-reply-forward-editior';
import UnifiedInboxEmailsActivityModal from '../../../../../../../unified-inbox/sub-components/unified-inbox-activity-modal';
import NoProspectDetailResultScreen from '../../../no-prospect-detail-result-screen/no-prospect-detail-result-screen';
import { Images } from '../../../../../../../../shared/app-constants';
import EmailListTabSkeleton from './email-list-tab-skeleton';
import EmailPreviewContent from '../../../../../../../../shared/components/email-preview/email-preview-content';
import { EmailDraft } from '../../../../../../../../shared/svg/email-draft';
import { EmailSent } from '../../../../../../../../shared/svg/email-sent';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import MasterLog from '../../../../../../../../shared/enums/master-log-enum';
import { stripChipTagsFromHtml } from '../../../../../../../../shared/editor/helpers';
import TruncatedTextWithTooltip from '../../../../../../../../shared/components/truncated-text-with-tooltip';
import { convertHtmlToText } from '../../../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import { getUserTimeZone } from '../../../../../../../../shared/utils/user-details';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { ExternalEmailSent } from '../../../../../../../../shared/svg/external-email-sent';
import GlobalRestrictionModal from '../../../../../../../home/components/global-restriction-modal';
import { getFormattedDate } from '../../../../../../../settings/components/users-and-teams/utils/get-formatted-date';

const renderAvatar = (fullName?: string) => {
  const parts = (fullName || '').trim().split(' ');
  return (
    <Avatar
      firstName={parts[0] || ''}
      lastName={parts.slice(1).join(' ')}
      size="lg"
      className="cs-avatar"
    />
  );
};

/**
 * Generates reply subject with "Re:" prefix if not present
 */
const getReplySubject = (subject: string) =>
  subject.startsWith('Re: ') ? subject : `Re: ${subject}`;

/**
 * Parses unifiedScheduled JSON string and converts it to StateValues format
 */
const parseUnifiedScheduledToStateValues = (
  unifiedScheduled: ProspectEmailsTabListItem,
  email: ProspectEmailsTabListItem,
): StateValues | null => {
  try {
    return {
      from: [email?.fromEmail],
      to: unifiedScheduled.to || [],
      cc: unifiedScheduled.cc || [],
      bcc: unifiedScheduled.bcc || [],
      content: unifiedScheduled.content || '',
      // Use unifiedScheduled.subject first (draft subject), fallback to email.subject
      subject: unifiedScheduled.subject || email.subject || '',
      emailThreadId: unifiedScheduled.emailThreadId || email.emailThreadId,
      emailId: unifiedScheduled.repliedOnEmailId || email.emailId,
      hashId: email.hashId || '',
      unifiedScheduledId: unifiedScheduled.unifiedScheduledId,
    };
  } catch (error) {
    console.error('Error parsing unifiedScheduled:', error);
    return null;
  }
};

// MAIN COMPONENT

const EmailListTab: React.FC<EmailListTabProps> = ({
  fields,
  emailListItems,
  selectedProspectId,
  selectedProspectData,
  fetchEmailListData,
  sendEmailProspectTab,
  deleteProspectEmailRequests,
  draftProspectEmailRequest,
  draftProspectEmailRequestStatus,
  resetGetProspectEmailsTabListingRequests,
  resetDeleteProspectEmailRequests,
  fetchEmailListDataStatus,
  deleteProspectEmailStatus,
  emailDraftData,
  getEmailAccounts,
  emailAccount,
  sendEmailProspectTabStatus,
  resetSendEmailProspectTabRequests,
  sendReplyDraftDiscardRequest,
  resetReplyForwardDraftRequests,
  resetEmailDraftDataRequest,
  resetEmailDraftDataRequestStatus,
  handleSendAttachmentUploadRequest,
  handleSendDeleteAttachmentRequest,
  handleResetUploadAttachmentRequest,
  handleResetDeleteAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  singleProspectReplyEmailRequest,
  setSelectedRepliedEmailId,
  replyEmailRequestStatus,
  sendGetEmailThreadActivity,
  resetUnifiedInboxEmailThreadActivity,
  unifiedInboxEmailThreadActivity,
  getEmailThreadActivityRequestStatus,
  getEmailThreadActivityRequestError,
  initialShowEmailEditor = false,
  onEmailEditorClose,
  handleImageUploadRequest,
  emailRequestStatus,
  resetReplyDraftDiscard,
  onActivityChange,
  activeTab,
  prospectActivityCount,
  editProspectAcitivityCount,
  isDemoAccount,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const userTimezone = getUserTimeZone();
  // Main compose editor state - always shown at top
  const [showReplyEditor, setShowReplyEditor] = useState(
    initialShowEmailEditor,
  );
  const [
    openedUnifiedScheduledDraftEmailId,
    setOpenedUnifiedScheduledDraftEmailId,
  ] = useState<number | null>(null);
  const [inlineEditorInputValue, setInlineEditorInputValue] = useState('');
  const [uploadAttachmentId, setUploadAttachmentId] = useState<number[]>(null);

  // Activity Modal State
  const [
    unifiedInboxEmailsActivityTab,
    setUnifiedInboxEmailsActivityTab,
  ] = useState<MasterLog>(MasterLog.UniboxEmailOpened);
  const [
    showUnifiedInboActivityModal,
    setShowUnifiedInboActivityModal,
  ] = useState(false);
  const [activityModalEmail, setActivityModalEmail] = useState<{
    emailId: number;
    subject: string;
    email: string;
  } | null>(null);
  const [attachmentId, setAttachmentId] = useState<number>(null);

  // Editor mode state (compose/reply/forward)
  const [inlineEditor, setInlineEditor] = useState<InlineEditorState | null>(
    null,
  );
  // Track which emailId has an editor open (reply/forward/draft)
  const [openEditorEmailId, setOpenEditorEmailId] = useState<number | null>(
    null,
  );
  // Track editor type for the open editor (reply/forward/draft)
  const [openEditorType, setOpenEditorType] = useState<
    'reply' | 'forward' | 'draft' | null
  >(null);
  const [deleteEmailId, setDeleteEmailId] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);

  // Track attachments for each inline draft editor (keyed by emailId)
  const [inlineDraftAttachments, setInlineDraftAttachments] = useState<
    Record<number, AttachmentType[]>
  >({});
  const [openPopoverEmailId, setOpenPopoverEmailId] = useState<number | null>(
    null,
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const [
    defaultDraftEditorFields,
    setDefaultDraftEditorFields,
  ] = useState<StateValues | null>(null);
  // Infinite scroll state
  const internalScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollContainerRef = internalScrollContainerRef;
  const isLoadingMoreRef = useRef<boolean>(false);
  const [allEmails, setAllEmails] = useState<ProspectEmailsTabListItem[]>([]);
  const [highlightedEmailIds, setHighlightedEmailIds] = useState<Set<number>>(
    new Set(),
  );
  // Track last emailId we highlighted from URL so we don't re-highlight on tab/action changes (ref avoids effect re-run so 2s timeout isn't cleared)
  const lastProcessedEmailIdFromUrlRef = useRef<number | null>(null);
  const emailContentRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const unifiedScheduledId = Array.isArray(allEmails)
    ? allEmails[0]?.unifiedScheduled?.unifiedScheduledId ?? 0
    : 0;

  // Sequence Id for the email currently opened in inline editor (reply / forward)
  const inlineEditorSequenceId =
    inlineEditor && Array.isArray(allEmails)
      ? allEmails.find((e) => e.emailId === inlineEditor.emailId)?.sequenceId
      : undefined;

  // Get pagination data from API response
  const apiCurrentPage = emailListItems?.meta?.currentPage || 1;
  const totalPages = emailListItems?.meta?.totalPages || 0;
  const pageLimit = emailListItems?.meta?.itemsPerPage || 25;

  // Calculate if there are more emails to load
  // Stop loading when currentPage >= totalPages
  const hasMoreEmails = useMemo(() => {
    if (!emailListItems || totalPages === 0) return false;
    return apiCurrentPage < totalPages;
  }, [emailListItems, apiCurrentPage, totalPages]);

  // Determine loading state
  const isLoadingMore =
    fetchEmailListDataStatus === RequestStatus.Pending && apiCurrentPage > 1;

  const timeZone = useMemo(() => getUserTimeZone(), []);

  const openedAtTooltipText = (email: ProspectEmailsTabListItem) =>
    email?.lastOpenedAt
      ? `Opened on ${formatDateTime(email?.lastOpenedAt, timeZone)}`
      : 'Not opened yet';

  const clickedAtTooltipText = (email: ProspectEmailsTabListItem) =>
    email?.lastClickedAt
      ? `Clicked on ${formatDateTime(email?.lastClickedAt, timeZone)}`
      : 'Not clicked yet';

  const handleActivityModalOpen = (
    tab: MasterLog,
    email: ProspectEmailsTabListItem,
  ) => {
    if (
      (tab === MasterLog.UniboxEmailOpened && !email?.lastOpenedAt) ||
      (tab === MasterLog.UniboxEmailLinkClicked && !email?.lastClickedAt)
    ) {
      return;
    }
    setUnifiedInboxEmailsActivityTab(tab);
    setActivityModalEmail({
      emailId: email?.emailId,
      subject: email?.subject || '',
      email: email?.sequenceName || email?.fromEmail || email?.fromName,
    });
    setShowUnifiedInboActivityModal(true);
  };

  const handleActivityModalClose = () => {
    setActivityModalEmail(null);
    setUnifiedInboxEmailsActivityTab(null);
    setShowUnifiedInboActivityModal(false);
  };

  // ============================================================================
  // COMPUTED VALUES & MEMOIZED DATA
  // ============================================================================

  // Merge new emails with existing ones for infinite scroll
  useEffect(() => {
    if (emailListItems && emailListItems?.items?.length > 0) {
      // If this is page 1, replace all emails
      if (apiCurrentPage === 1) {
        setAllEmails(emailListItems?.items);
      } else {
        // Subsequent pages: append new emails (avoid duplicates)
        setAllEmails((prev) => {
          const existingIds = new Set(prev?.map((e) => e?.emailId));
          const newEmails = emailListItems?.items?.filter(
            (e) => !existingIds?.has(e?.emailId),
          );
          if (newEmails.length > 0) {
            return [...prev, ...newEmails];
          }
          return prev;
        });
      }
    } else if (
      emailListItems &&
      emailListItems?.items?.length === 0 &&
      apiCurrentPage === 1
    ) {
      // If we get empty results on page 1, clear the list
      setAllEmails([]);
    }
  }, [emailListItems, apiCurrentPage]);

  // Filter out draft emails and ensure we have valid array
  // Also hide email if its editor is open
  const emailsToRender = useMemo(
    () => (allEmails?.length > 0 ? allEmails : []),
    [allEmails],
  );

  // Check if the email being deleted has replies or is part of a thread
  // const hasRepliesOrThread = useMemo(() => {
  //   if (!deleteEmailId) return false;

  //   const emailToDelete = allEmails?.find(
  //     (email) => email.emailId === deleteEmailId,
  //   );

  //   if (!emailToDelete) return false;

  //   return allEmails?.some(
  //     (email) =>
  //       email?.emailId !== deleteEmailId &&
  //       (email?.repliedOnEmailId === deleteEmailId ||
  //         email?.emailThreadId === emailToDelete?.emailThreadId),
  //   );
  // }, [deleteEmailId, allEmails]);

  // Find draft email data if exists
  const draftEmailData = useMemo(
    () => allEmails?.find((item) => item?.isDraft) || null,
    [allEmails],
  );

  // Filter connected email accounts (status === 1)
  const filterConnectedEmailAccounts = useMemo(
    () =>
      emailAccount?.emailAccounts?.map((i) => ({
        id: i?.id,
        name: i?.fromName,
        email: i?.fromEmail,
        status: i?.status,
        emailServiceProvider: i?.emailServiceProvider,
      })) || [],
    [emailAccount],
  );
  const defaultFromEmail = filterConnectedEmailAccounts?.find(
    (i) => i?.status === 1,
  );

  const draftEditorDefaults = useMemo(
    () => ({
      from: defaultFromEmail ? [defaultFromEmail] : [],
      to: selectedProspectData?.email ? [selectedProspectData.email] : [],
      cc: [] as string[],
      bcc: [] as string[],
      content: '',
      subject: '',
      emailThreadId: 0,
      emailId: 0,
      hashId: '',
    }),
    [draftEmailData, selectedProspectData, filterConnectedEmailAccounts],
  );

  // Default draft editor fields for reply mode
  const replyDraftEditorFields = useMemo(() => {
    if (inlineEditor?.type === 'reply' && inlineEditor) {
      // Find the email being replied to
      const emailBeingRepliedTo = emailsToRender.find(
        (e) => e?.emailId === inlineEditor?.emailId,
      );

      const hasEailDraft =
        emailDraftData?.unifiedScheduledId &&
        emailDraftData?.repliedOnEmailId === inlineEditor?.emailId;

      // Determine the 'to' field based on fromProspectId/toProspectId
      let toField: string[] = [];
      if (emailBeingRepliedTo) {
        if (emailBeingRepliedTo.toProspectId) {
          toField = emailBeingRepliedTo?.to || [];
        } else if (emailBeingRepliedTo?.fromProspectId) {
          toField = [emailBeingRepliedTo?.fromEmail];
        }
      }
      // Fallback to inlineEditor.to if available, or selectedProspectData.email
      if (toField.length === 0) {
        toField = inlineEditor?.to
          ? [inlineEditor?.to]
          : selectedProspectData?.email
          ? [selectedProspectData.email]
          : [];
      }

      return {
        from: [] as string[],
        to: toField.filter(Boolean),
        cc: [] as string[],
        bcc: [] as string[],
        content: '',
        subject: inlineEditor.subject || '',
        emailThreadId: inlineEditor.emailThreadId || 0,
        emailId: inlineEditor.emailId || 0,
        hashId: inlineEditor.hashId || '',
        ...(hasEailDraft
          ? { unifiedScheduledId: emailDraftData?.unifiedScheduledId }
          : {}),
      };
    }
    return null;
  }, [inlineEditor, emailsToRender, selectedProspectData, emailDraftData]);

  // Construct unifiedSchedule for inline reply editor when emailDraftData exists
  const inlineReplyUnifiedSchedule = useMemo(():
    | UnifiedSchedule
    | undefined => {
    if (
      openEditorType === 'reply' &&
      openEditorEmailId &&
      emailDraftData?.unifiedScheduledId &&
      emailDraftData?.repliedOnEmailId === openEditorEmailId
    ) {
      // Find the email being replied to from allEmails (since it might be filtered out)
      const emailBeingRepliedTo = allEmails.find(
        (e) => e.emailId === openEditorEmailId,
      );

      if (emailBeingRepliedTo && emailDraftData) {
        return {
          fromName: emailBeingRepliedTo.fromName || '',
          fromEmail: emailBeingRepliedTo.fromEmail || '',
          unifiedScheduledId: emailDraftData.unifiedScheduledId,
          repliedOnEmailId: emailDraftData.repliedOnEmailId,
          emailThreadId: emailDraftData.emailThreadId,
          content: emailDraftData.content || '',
          status: EmailScheduleStatus.Draft,
          to: emailDraftData.to || [],
          cc: emailDraftData.cc || [],
          bcc: emailDraftData.bcc || [],
          attachments: emailBeingRepliedTo?.unifiedScheduled?.attachments,
          templateId: emailDraftData.templateId || undefined,
          sendEmailIfReplied: emailDraftData.sendEmailIfReplied || false,
        };
      }
    }
    return undefined;
  }, [openEditorType, openEditorEmailId, emailDraftData, allEmails]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  useEffect(() => {
    if (uploadedAttachment?.attachmentId) {
      setUploadAttachmentId((prev) => [
        ...(prev ?? []),
        uploadedAttachment.attachmentId,
      ]);
    }
  }, [uploadedAttachment]);

  // Sync showReplyEditor with initialShowEmailEditor prop
  useEffect(() => {
    if (initialShowEmailEditor) {
      setShowReplyEditor(true);
    } else {
      // Close editor when prop changes to false (e.g., when switching away from email tab)
      setShowReplyEditor(false);
    }
  }, [initialShowEmailEditor]);

  // Reset editor to compose mode (editor always visible)
  const handleHideEditor = useCallback(() => {
    setInlineEditor(null);
    setInlineEditorInputValue('');
  }, []);

  // Handle edit draft click - opens inline editor above the email
  const handleEditDraftClick = useCallback(
    (email: ProspectEmailsTabListItem) => {
      if (!email.isDraft) return;
      // Check if editor is already open for this draft email
      // if (openEditorEmailId === email?.emailId && openEditorType === 'draft') {
      //   // Editor already open, just scroll to it
      //   setTimeout(() => {
      //     const emailElement = emailContentRefs.current[email.emailId]?.closest(
      //       '.email-list-single-email-conatiner',
      //     ) as HTMLElement;
      //     emailElement?.scrollIntoView({
      //       behavior: 'smooth',
      //       block: 'center',
      //     });
      //   }, 100);
      //   return;
      // }

      // Find matching fromEmail account from filterConnectedEmailAccounts
      const matchedFromEmail = email?.fromEmail
        ? filterConnectedEmailAccounts?.find(
            (acc) => acc?.email === email?.fromEmail,
          ) || defaultFromEmail
        : defaultFromEmail;
      setDefaultDraftEditorFields({
        from: matchedFromEmail ? [matchedFromEmail] : [],
        to: (email.to || []) as string[],
        cc: (email?.unifiedScheduled?.cc ?? email.cc) as string[],
        bcc: (email?.unifiedScheduled?.bcc ?? email.bcc) as string[],
        subject: email.subject || '',
        content: email?.unifiedScheduled?.content || email?.content || '',
        emailThreadId: email.emailThreadId || 0,
        emailId: email.emailId || 0,
        hashId: email.hashId || '',
        unifiedScheduledId: email?.unifiedScheduled?.unifiedScheduledId,
        attachments:
          email?.unifiedScheduled?.attachments || email?.attachments || [],
      });
      setInlineEditor(null); // Clear inline editor state
      setInlineEditorInputValue('');
      setSelectedRepliedEmailId(email.emailId);
      setOpenEditorEmailId(email.emailId);
      setOpenEditorType('draft');
      setShowReplyEditor(false); // Don't show top editor
      // Scroll to the editor container (draft email is hidden, editor replaces it)
      setTimeout(() => {
        // Find the editor container using the data attribute
        const editorContainer = document.querySelector(
          `.email-list-inline-editor-container[data-email-id="${email.emailId}"]`,
        ) as HTMLElement;
        if (editorContainer) {
          editorContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 150);
    },
    [
      openEditorEmailId,
      openEditorType,
      filterConnectedEmailAccounts,
      defaultFromEmail,
    ],
  );

  // Open main compose editor at top (only for new emails)
  const handleOpenEditor = useCallback(() => {
    // If draft exists, open draft editor inline instead of at top
    if (draftEmailData?.isDraft) {
      handleEditDraftClick(draftEmailData);
      return;
    }
    // Clear any inline editor state
    setInlineEditorInputValue('');
    setInlineEditor(null);
    setOpenEditorEmailId(null);
    setOpenEditorType(null);
    setDefaultDraftEditorFields(null); // Clear any draft data for new compose
    setShowReplyEditor(true); // Show top editor for new compose
  }, [draftEmailData, handleEditDraftClick]);

  // Process emails to insert draft emails above emails with unifiedScheduled data
  const processedEmails = useMemo(() => {
    if (!allEmails || allEmails.length === 0) return [];

    const result: ProspectEmailsTabListItem[] = [];

    allEmails.forEach((email) => {
      // Check if email has isDraft: false and unifiedScheduled has data
      const hasUnifiedScheduledDraft =
        email?.isDraft === false && email?.unifiedScheduled;

      if (hasUnifiedScheduledDraft && email.unifiedScheduled) {
        // Create a draft email object from unifiedScheduled data
        // Use a unique negative ID to distinguish from the original email
        const draftEmailId =
          email.unifiedScheduled.unifiedScheduledId && -Math.abs(email.emailId); // Use negative unifiedScheduledId to make it unique

        const draftEmail: ProspectEmailsTabListItem = {
          ...email,
          isDraft: email?.isDraft,
          emailId: draftEmailId,
          subject: email.subject || '', // Use subject from original email since UnifiedSchedule doesn't have subject
          content: email.unifiedScheduled.content || '',
          to: email.unifiedScheduled.to || email.to || [],
          cc: email.unifiedScheduled.cc || email.cc || [],
          bcc: email.unifiedScheduled.bcc || email.bcc || [],
          attachments: (email.unifiedScheduled.attachments ||
            email.attachments ||
            []) as [],
          unifiedScheduled: email?.unifiedScheduled,
          repliedEmail: true,
        };

        // Add the draft email first
        result.push(draftEmail);
      }

      // Add the actual email (it will be shown normally, not as draft)
      result.push(email);
    });

    return result;
  }, [allEmails]);

  // Handle reply click - sets up inline editor above the email
  const handleReplyClick = useCallback(
    (email: ProspectEmailsTabListItem) => {
      // Check if editor is already open for this email
      if (openEditorEmailId === email?.emailId && openEditorType === 'reply') {
        // Editor already open, just scroll to it
        setTimeout(() => {
          const emailElement = emailContentRefs.current[email.emailId]?.closest(
            '.email-list-single-email-conatiner',
          ) as HTMLElement;
          emailElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
        return;
      }

      const replyDraftEmail = processedEmails?.find(
        (item) =>
          item?.emailId === -email?.emailId || item?.emailId === email?.emailId,
      )?.emailId;

      setSelectedRepliedEmailId(email?.emailId);
      setDefaultDraftEditorFields(null); // Clear any draft data for new reply
      setInlineEditor({
        type: 'reply',
        emailId: email?.emailId,
        emailThreadId: email.emailThreadId,
        hashId: email.hashId,
        subject: getReplySubject(email.subject || ''),
        to: String(email?.to) || '',
      });
      setOpenEditorEmailId(replyDraftEmail);
      setOpenEditorType('reply');
      setShowReplyEditor(false); // Don't show top editor
      // Scroll to the editor (which is above the email)
      setTimeout(() => {
        const emailElement = emailContentRefs.current[email.emailId]?.closest(
          '.email-list-single-email-conatiner',
        ) as HTMLElement;
        if (emailElement) {
          // Find the editor container above the email
          const editorContainer = emailElement.previousElementSibling as HTMLElement;
          if (
            editorContainer &&
            editorContainer.classList.contains(
              'email-list-inline-editor-container',
            )
          ) {
            editorContainer.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          } else {
            // Fallback: scroll to the email itself
            emailElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }, 100);
    },
    [openEditorEmailId, openEditorType, allEmails],
  );

  // Handle forward click - sets up inline editor above the email
  const handleForwardClick = useCallback(
    (email: ProspectEmailsTabListItem) => {
      // Check if forward editor is already open for this email
      if (
        openEditorEmailId === email?.emailId &&
        openEditorType === 'forward'
      ) {
        // Forward editor already open, just scroll to it
        setTimeout(() => {
          const emailElement = emailContentRefs.current[email.emailId]?.closest(
            '.email-list-single-email-conatiner',
          ) as HTMLElement;
          emailElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
        return;
      }

      setDefaultDraftEditorFields(null); // Clear any draft data for new forward
      setInlineEditor({
        type: 'forward',
        emailId: email.emailId,
        emailThreadId: email.emailThreadId,
        hashId: email.hashId,
        subject: email.subject || '',
        to: '',
      });
      setOpenEditorEmailId(email?.emailId);
      setOpenEditorType('forward');
      setShowReplyEditor(false); // Don't show top editor
      // Scroll to the email position
      setTimeout(() => {
        const emailIndex = allEmails.findIndex(
          (e) => e.emailId === email.emailId,
        );
        const container = scrollContainerRef.current;
        if (container && emailIndex > 0) {
          const previousEmail = allEmails[emailIndex - 1];
          const previousElement = emailContentRefs.current[
            previousEmail?.emailId
          ]?.closest('.email-list-single-email-conatiner') as HTMLElement;
          previousElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        }
      }, 100);
    },
    [openEditorEmailId, openEditorType, allEmails],
  );

  // Handle edit unifiedScheduled draft click - opens inline editor above the email
  const handleEditUnifiedScheduledDraftClick = useCallback(
    (email: ProspectEmailsTabListItem) => {
      if (!email.repliedEmail) return;
      // Check if reply editor is already open for this email
      if (openEditorEmailId === email?.emailId && openEditorType === 'reply') {
        // Editor already open, just scroll to it
        setTimeout(() => {
          // Find the editor container using the data attribute
          const editorContainer = document.querySelector(
            `.email-list-inline-editor-container[data-email-id="${email.emailId}"]`,
          ) as HTMLElement;
          if (editorContainer) {
            editorContainer.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
        return;
      }
      // const hasunifiedScheduledId = ema
      // Set up reply editor inline above the email
      setSelectedRepliedEmailId(email?.emailId);
      setDefaultDraftEditorFields(null); // Clear any draft data for new reply
      setOpenedUnifiedScheduledDraftEmailId(null); // Ensure old inline editor is closed
      setInlineEditor({
        type: 'reply',
        emailId: email?.emailId,
        emailThreadId: email.emailThreadId,
        hashId: email.hashId,
        subject: getReplySubject(email.subject || ''),
        to: String(email?.to) || '',
        // unifiedScheduledId: email?.unifiedScheduledId
      });
      setOpenEditorEmailId(email?.emailId);
      setOpenEditorType('reply');
      setShowReplyEditor(false); // Don't show top editor
      // Scroll to the editor container (draft email is hidden, editor replaces it)
      setTimeout(() => {
        // Find the editor container using the data attribute
        const editorContainer = document.querySelector(
          `.email-list-inline-editor-container[data-email-id="${email.emailId}"]`,
        ) as HTMLElement;
        if (editorContainer) {
          editorContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 150);
    },
    [openEditorEmailId, openEditorType, allEmails],
  );

  // Cancel editor (reply/forward/draft) - reset state
  const handleCancelInlineEditor = useCallback(() => {
    setInlineEditor(null);
    setInlineEditorInputValue('');
    setOpenEditorEmailId(null);
    setOpenEditorType(null);
    setShowReplyEditor(false);
    setSelectedRepliedEmailId(null); // Reset selected replied email ID
    setDefaultDraftEditorFields(null); // Clear draft editor fields for clean state
    setAttachments([]); // Clear attachments
    setUploadAttachmentId(null); // Clear upload attachment ID
    if (onEmailEditorClose) {
      onEmailEditorClose();
    }
  }, [onEmailEditorClose, setSelectedRepliedEmailId]);

  // Handle delete email confirmation
  const handleDeleteEmail = useCallback(
    (emailId: number, removeThreadChecked?: boolean) => {
      const shouldThreadDelete = removeThreadChecked && true;
      deleteProspectEmailRequests?.(emailId, shouldThreadDelete);
      setDeleteEmailId(null);
    },
    [deleteProspectEmailRequests],
  );

  // Handle send email (main compose or inline reply/forward)
  const handleSend = useCallback(
    (
      data: {
        to: string[];
        cc: string[];
        bcc: string[];
        from: string | EmailListFromOpt;
        content: string;
        attachments: AttachmentType[];
        emailThreadId: number;
        emailId: number;
        subject: string;
      },
      isInlineReply?: boolean,
      isInlineForward?: boolean,
      targetEmailId?: number,
      targetEmailThreadId?: number,
      // unifiedScheduledId?: number,
    ) => {
      if (isDemoAccount) {
        setShowGlobalRestrictionModal(true);
        return;
      }
      const finalEmailThreadId =
        isInlineReply || isInlineForward
          ? targetEmailThreadId
          : emailDraftData?.emailThreadId;
      const finalEmailId =
        isInlineReply || isInlineForward
          ? targetEmailId
          : emailDraftData?.repliedOnEmailId;
      sendEmailProspectTab?.({
        emailThreadId:
          defaultDraftEditorFields?.emailThreadId || finalEmailThreadId,
        emailId: defaultDraftEditorFields?.emailId || finalEmailId,
        content: stripChipTagsFromHtml(data?.content),
        subject: convertHtmlToText(stripChipTagsFromHtml(data?.subject)),
        ...(data?.cc?.length > 0 ? { cc: data?.cc } : {}),
        ...(data?.bcc?.length > 0 ? { bcc: data?.bcc } : {}),
        to: data.to,
        ...(attachments?.length > 0
          ? { attachmentIds: attachments?.map((item) => item?.attachmentId) }
          : {}),
        ...(unifiedScheduledId ? { unifiedScheduledId } : {}),
      });
    },
    [emailDraftData, sendEmailProspectTab, uploadAttachmentId, attachments],
  );

  // Handle send for inline draft editors
  const handleInlineDraftSend = useCallback(
    (
      emailId: number,
      data: {
        to: string[];
        cc: string[];
        bcc: string[];
        from: string | EmailListFromOpt;
        content: string;
        attachments: AttachmentType[];
        emailThreadId: number;
        subject: string;
      },
      unifiedScheduledId?: number,
    ) => {
      const emailAttachments = inlineDraftAttachments[emailId] || [];
      const attachmentIds = emailAttachments
        .map((att) => att.attachmentId)
        .filter(Boolean);

      sendEmailProspectTab?.({
        emailThreadId: data.emailThreadId,
        emailId,
        content: stripChipTagsFromHtml(data?.content),
        subject: convertHtmlToText(stripChipTagsFromHtml(data?.subject)),
        to: data.to,
        ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
        ...(unifiedScheduledId ? { unifiedScheduledId } : {}),
      });
    },
    [sendEmailProspectTab, inlineDraftAttachments],
  );

  // const handleReplyEmail = useCallback(
  //   (data: {
  //     to: string[];
  //     cc: string[];
  //     bcc: string[];
  //     from: string | EmailListFromOpt;
  //     content: string;
  //     attachments: AttachmentType[];
  //     emailThreadId: number;
  //     emailId: number;
  //   }) => {
  //     singleProspectReplyEmailRequest({
  //       emailId: emailDraftData?.repliedOnEmailId,
  //       emailThreadId: emailDraftData?.emailThreadId,
  //       content: stripChipTagsFromHtml(data?.content),
  //       to: data.to,
  //       ...(data?.cc?.length > 0 ? { cc: data.cc } : {}),
  //       ...(data?.bcc?.length > 0 ? { bcc: data.bcc } : {}),
  //       ...(attachmentId ? { attachmentIds: uploadAttachmentId } : {}),
  //     });
  //   },
  //   [inlineEditor, uploadAttachmentId, emailDraftData?.repliedOnEmailId],
  // );

  // Convert ProspectEmailsTabListItem to ReplyForwardEmailPreview format
  const convertToReplyForwardEmailPreview = useCallback(
    (email: ProspectEmailsTabListItem): ReplyForwardEmailPreview => ({
      senderName: email.fromName || '',
      fromEmail: email.fromEmail || '',
      to: email.to || [],
      subject: email.subject || '',
      sentDate: email.sentAt || '',
      content: email.content || '',
      cc: email.cc || [],
      bcc: email.bcc || [],
      attachments: email.attachments || [],
      emailId: email.emailId,
      emailThreadId: String(email.emailThreadId),
      toProspectId: selectedProspectId || 0,
      fromProspectId: email.userId || 0,
    }),
    [selectedProspectId],
  );

  // ============================================================================
  // SIDE EFFECTS (useEffect)
  // ============================================================================

  // Initialize: Fetch email accounts on mount
  useEffect(() => {
    getEmailAccounts({});
  }, []);

  useEffect(() => {
    if (uploadedAttachment !== null) {
      setAttachmentId(uploadedAttachment?.attachmentId);
    }
  }, [uploadedAttachment]);

  // Fetch email list when prospect changes
  useEffect(() => {
    setAllEmails([]);
    fetchEmailListData?.({
      prospectId: selectedProspectId,
      page: 1,
      limit: pageLimit,
    });
  }, [selectedProspectId]);

  // Handle draft email: Auto-open editor if draft exists
  // useEffect(() => {
  //   if (draftEmailData) {
  //     setShowReplyEditor(true);
  //   }
  // }, [draftEmailData]);

  // Handle load more emails
  const handleLoadMore = useCallback(() => {
    if (
      !selectedProspectId ||
      !hasMoreEmails ||
      isLoadingMore ||
      isLoadingMoreRef.current
    ) {
      return;
    }

    isLoadingMoreRef.current = true;
    const nextPage = apiCurrentPage + 1;
    fetchEmailListData?.({
      prospectId: selectedProspectId,
      page: nextPage,
      limit: pageLimit,
    });
  }, [
    selectedProspectId,
    hasMoreEmails,
    isLoadingMore,
    apiCurrentPage,
    pageLimit,
    fetchEmailListData,
  ]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    // Don't trigger load more if there are no emails
    if (!emailsToRender || emailsToRender.length === 0) return;

    // Prevent duplicate calls using ref
    if (isLoadingMoreRef.current) return;

    // Don't load if there are no more emails
    if (!hasMoreEmails) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Load more when the user is 200px from the bottom
    const threshold = 200;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + threshold;

    if (isNearBottom && hasMoreEmails && !isLoadingMore) {
      handleLoadMore();
    }
  }, [
    hasMoreEmails,
    isLoadingMore,
    handleLoadMore,
    scrollContainerRef,
    emailsToRender,
  ]);

  // Update ref when isLoadingMore changes
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Reset loading ref when request completes
  useEffect(() => {
    if (fetchEmailListDataStatus === RequestStatus.Succeeded) {
      isLoadingMoreRef.current = false;
    } else if (fetchEmailListDataStatus === RequestStatus.Failed) {
      isLoadingMoreRef.current = false;
    }
  }, [fetchEmailListDataStatus]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Close popover on scroll
  useEffect(() => {
    if (openPopoverEmailId === null) return;

    const handleScrollClose = () => {
      setOpenPopoverEmailId(null);
    };

    const container = scrollContainerRef?.current;

    if (container) {
      container.addEventListener('scroll', handleScrollClose, {
        passive: true,
      });
    }

    window.addEventListener('scroll', handleScrollClose, {
      passive: true,
      capture: true,
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScrollClose);
      }
      window.removeEventListener('scroll', handleScrollClose, {
        capture: true,
      });
    };
  }, [openPopoverEmailId]);

  // Handle request statuses: Reset requests and refetch data on success
  useEffect(() => {
    // Handle fetch email list status
    executeOnRequestStatus({
      status: fetchEmailListDataStatus,
      onSuccess: () => {
        resetGetProspectEmailsTabListingRequests?.();
        setInlineEditorInputValue('');
        setDefaultDraftEditorFields(null);
      },
      onFailed: () => {
        resetGetProspectEmailsTabListingRequests?.();
        setDefaultDraftEditorFields(null);
      },
    });

    // Handle delete email status
    executeOnRequestStatus({
      status: deleteProspectEmailStatus,
      onSuccess: () => {
        resetDeleteProspectEmailRequests?.();
        setAllEmails([]); // Reset emails to reload from page 1
        fetchEmailListData?.({
          prospectId: selectedProspectId,
          page: 1,
          limit: pageLimit,
        });
        editProspectAcitivityCount({
          ...prospectActivityCount,
          emails:
            prospectActivityCount?.emails >= 0
              ? (Number(prospectActivityCount?.emails) || 0) - 1
              : 1,
        });
        toaster.success('Email has been deleted successfully.', {
          theme: Theme.New,
        });
        onActivityChange?.(true);
      },
      onFailed: () => resetDeleteProspectEmailRequests?.(),
    });

    // Handle send email status
    executeOnRequestStatus({
      status: sendEmailProspectTabStatus?.status,
      onSuccess: () => {
        handleHideEditor();
        handleCancelInlineEditor();
        resetSendEmailProspectTabRequests?.();
        setAllEmails([]); // Reset emails to reload from page 1
        fetchEmailListData?.({
          prospectId: selectedProspectId,
          page: 1,
          limit: pageLimit,
        });
        resetEmailDraftDataRequest();
        setAttachments([]);
        setInlineDraftAttachments({}); // Clear all inline draft attachments
        setDefaultDraftEditorFields(null);
        setShowReplyEditor(false);
        setUploadAttachmentId(null);
        setOpenedUnifiedScheduledDraftEmailId(null); // Close any opened unifiedScheduled draft editors
        setOpenEditorEmailId(null);
        setOpenEditorType(null);
        editProspectAcitivityCount({
          ...prospectActivityCount,
          emails:
            prospectActivityCount?.emails >= 0
              ? (Number(prospectActivityCount?.emails) || 0) + 1
              : 1,
        });
        if (onEmailEditorClose) {
          onEmailEditorClose();
        }
        onActivityChange?.(true);
      },
      onFailed: () => {
        resetSendEmailProspectTabRequests?.();
        resetEmailDraftDataRequest();
        setAttachments([]);
      },
    });
  }, [
    fetchEmailListDataStatus,
    deleteProspectEmailStatus,
    sendEmailProspectTabStatus?.status,
    onActivityChange,
    selectedProspectId,
    fetchEmailListData,
    resetGetProspectEmailsTabListingRequests,
    resetDeleteProspectEmailRequests,
    resetSendEmailProspectTabRequests,
    handleHideEditor,
    handleCancelInlineEditor,
    pageLimit,
    emailDraftData?.repliedOnEmailId,
    prospectActivityCount,
    editProspectAcitivityCount,
  ]);

  // Reply Send Request Status
  useEffect(() => {
    executeOnRequestStatus({
      status: replyEmailRequestStatus?.status,
      onSuccess: () => {
        handleHideEditor();
        handleCancelInlineEditor();
        fetchEmailListData?.({
          prospectId: selectedProspectId,
          page: 1,
          limit: pageLimit,
        });
        resetEmailDraftDataRequest();
        setAttachments([]);
        setInlineDraftAttachments({}); // Clear all inline draft attachments
        setDefaultDraftEditorFields(null);
        setShowReplyEditor(false);
        setUploadAttachmentId(null);
        setOpenedUnifiedScheduledDraftEmailId(null); // Close any opened unifiedScheduled draft editors
        setOpenEditorEmailId(null);
        setOpenEditorType(null);
        resetReplyForwardDraftRequests(inlineEditor?.emailId);
        editProspectAcitivityCount({
          ...prospectActivityCount,
          emails:
            prospectActivityCount?.emails >= 0
              ? (Number(prospectActivityCount?.emails) || 0) + 1
              : 1,
        });
        toaster?.success(`${replyEmailRequestStatus?.message}`);

        if (onEmailEditorClose) {
          onEmailEditorClose();
        }
      },
      onFailed: () => {
        resetReplyForwardDraftRequests(inlineEditor?.emailId);
      },
    });
  }, [
    replyEmailRequestStatus?.status,
    inlineEditor?.emailId,
    onEmailEditorClose,
  ]);

  // Discard draft Email Status
  useEffect(() => {
    executeOnRequestStatus({
      status: resetEmailDraftDataRequestStatus,
      onSuccess: () => {
        handleHideEditor();
        handleCancelInlineEditor();
        fetchEmailListData?.({
          prospectId: selectedProspectId,
          page: 1,
          limit: pageLimit,
        });
        resetEmailDraftDataRequest();
        setAttachments([]);
        setInlineDraftAttachments({}); // Clear all inline draft attachments
        setDefaultDraftEditorFields(null);
        setShowReplyEditor(false);
        setOpenedUnifiedScheduledDraftEmailId(null); // Close any opened unifiedScheduled draft editors
        setOpenEditorEmailId(null);
        setOpenEditorType(null);
        // this is for reset discard draft email
        resetReplyDraftDiscard(inlineEditor?.emailId);
        resetReplyForwardDraftRequests(inlineEditor?.emailId);

        if (onEmailEditorClose) {
          onEmailEditorClose();
        }
      },
      onFailed: () => {
        resetReplyForwardDraftRequests(inlineEditor?.emailId);
      },
    });
  }, [
    resetEmailDraftDataRequestStatus,
    inlineEditor?.emailId,
    onEmailEditorClose,
  ]);

  useEffect(() => {
    // Don't set draft fields if we're in reply or forward mode
    // This allows reply/forward to override draft data
    if (inlineEditor?.type === 'reply' || inlineEditor?.type === 'forward') {
      return;
    }

    if (
      emailDraftData ||
      draftEmailData?.isDraft === true ||
      unifiedScheduledId
    ) {
      // Find matching fromEmail account from filterConnectedEmailAccounts
      const matchedFromEmail = draftEmailData?.fromEmail
        ? filterConnectedEmailAccounts?.find(
            (acc) => acc.email === draftEmailData?.fromEmail,
          ) || defaultFromEmail
        : defaultFromEmail;
      setDefaultDraftEditorFields({
        from: matchedFromEmail ? [matchedFromEmail] : [],
        to: draftEmailData?.unifiedScheduled?.to ?? emailDraftData?.to ?? [],
        cc: draftEmailData?.unifiedScheduled?.cc ?? emailDraftData?.cc,
        bcc: draftEmailData?.unifiedScheduled?.bcc ?? emailDraftData?.bcc,
        subject: draftEmailData?.subject ?? emailDraftData?.subject ?? '',
        content:
          draftEmailData?.unifiedScheduled?.content ??
          emailDraftData?.content ??
          '',
        emailThreadId:
          draftEmailData?.emailThreadId ?? emailDraftData?.emailThreadId,
        emailId:
          draftEmailData?.emailId ?? emailDraftData?.repliedOnEmailId ?? 0,
        hashId: draftEmailData?.hashId ?? '',
        unifiedScheduledId:
          draftEmailData?.unifiedScheduled?.unifiedScheduledId ??
          emailDraftData?.unifiedScheduledId ??
          unifiedScheduledId,
        attachments: draftEmailData?.attachments ?? emailDraftData?.attachments,
      });
    }
  }, [
    emailDraftData,
    draftEmailData?.isDraft,
    unifiedScheduledId,
    filterConnectedEmailAccounts,
    defaultFromEmail,
    inlineEditor?.type,
  ]);

  // Remove emailId from URL query params without refreshing
  const removeEmailIdFromUrl = () => {
    if (typeof window === 'undefined') return;
    const queryParams = new URLSearchParams(window?.location?.search);
    if (queryParams?.has('emailId')) {
      queryParams?.delete('emailId');
      const newUrl = queryParams?.toString()
        ? `${window?.location?.pathname}?${queryParams?.toString()}`
        : window?.location?.pathname;
      window?.history?.replaceState(null, '', newUrl);
    }
  };

  // Reset last processed emailId when prospect changes (so new prospect's emailId in URL is highlighted)
  useEffect(() => {
    lastProcessedEmailIdFromUrlRef.current = null;
  }, [selectedProspectId]);

  // Handle highlighting emails based on emailId from URL (only once per emailId per prospect, like tasks' lastProcessedUrlTaskId)
  useEffect(() => {
    if (!emailsToRender?.length) return;

    const url = window?.location;
    const urlParams = new URLSearchParams(url?.search);
    const emailIdParam = urlParams?.get('emailId');

    if (!emailIdParam) return;

    // Parse emailId from URL param
    const emailId = Number(emailIdParam?.trim());
    // eslint-disable-next-line no-restricted-globals
    if (!emailId || isNaN(emailId)) return;

    // Only process if we haven't already highlighted this emailId for this prospect (prevents re-highlight on tab change / actions)
    if (lastProcessedEmailIdFromUrlRef.current === emailId) return;

    // Find matching email
    const matchingEmail = emailsToRender?.find(
      (email) => email?.emailId === emailId,
    );
    if (!matchingEmail) return;

    lastProcessedEmailIdFromUrlRef.current = emailId;
    setHighlightedEmailIds(new Set([emailId]));

    // Scroll to highlighted email
    const scrollTimeoutId = setTimeout(() => {
      const emailElement = emailContentRefs.current[emailId]?.closest(
        '.email-list-single-email-conatiner',
      ) as HTMLElement;
      emailElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 200);

    // Remove highlight and emailId from URL after 2 seconds
    const highlightTimeoutId = setTimeout(() => {
      setHighlightedEmailIds(new Set());
      removeEmailIdFromUrl();
    }, 2000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(scrollTimeoutId);
      clearTimeout(highlightTimeoutId);
    };
  }, [emailsToRender]);

  // ============================================================================
  // RENDER
  // ============================================================================
  // Filter out draft emails for display
  const nonDraftEmails = emailsToRender.filter((email) => !email.isDraft);

  // Processed emails for rendering (emails remain visible even when editor is open)
  const processedEmailsToRender = useMemo(
    () => (processedEmails?.length > 0 ? processedEmails : []),
    [processedEmails],
  );

  // Find the email with open editor from processedEmails (includes draft copies)
  // This hook must be called before any early returns
  const emailWithOpenEditor = useMemo(() => {
    if (!openEditorEmailId) return null;
    // First check processedEmails (includes draft copies with unique IDs)
    const emailInProcessed = processedEmails.find(
      (e) => e.emailId === openEditorEmailId,
    );
    if (emailInProcessed) return emailInProcessed;
    // Fallback to allEmails for regular emails
    return allEmails.find((e) => e.emailId === openEditorEmailId) || null;
  }, [openEditorEmailId, processedEmails, allEmails]);

  // Show loading skeleton while fetching (after all hooks are called)
  if (getIsRequestPending(fetchEmailListDataStatus)) {
    return <EmailListTabSkeleton />;
  }

  if (activeTab !== 'email') {
    return null;
  }

  return (
    <div className="email-list-tab-container">
      {/* Only show top editor for new compose (no specific email selected) */}
      {!showReplyEditor && !openEditorEmailId && (
        <div
          className="email-list-tab-header-send-email-container"
          role="button"
          tabIndex={0}
          {...accessibleOnClick(handleOpenEditor)}
          onKeyDown={(e) =>
            (e.key === 'Enter' || e.key === ' ') && handleOpenEditor()
          }
        >
          <Mail width={16} height={16} />
          <span className="email-list--header-email">Send Email</span>
        </div>
      )}
      {/* Top editor only for new compose (when openEditorEmailId is null) */}
      {showReplyEditor && !openEditorEmailId && (
        <>
          <CustomReplyForwardEditor
            key="editor-compose-new"
            label={undefined}
            action={undefined}
            showFromField={undefined}
            editorDefaultFields={draftEditorDefaults}
            onSend={handleSend}
            onDiscard={handleCancelInlineEditor}
            inlineEditorInputValue={inlineEditorInputValue}
            setInlineEditorInputValue={setInlineEditorInputValue}
            mergeTagOptions={fields}
            fromAddresses={filterConnectedEmailAccounts}
            draftProspectEmailRequest={draftProspectEmailRequest}
            draftProspectEmailRequestStatus={draftProspectEmailRequestStatus}
            selectedProspectId={selectedProspectId}
            sendReplyDraftDiscardRequest={sendReplyDraftDiscardRequest}
            defaultDraftEditorFields={defaultDraftEditorFields}
            sequenceId={undefined}
            hashId={undefined}
            handleSendAttachmentUploadRequest={
              handleSendAttachmentUploadRequest
            }
            handleSendDeleteAttachmentRequest={
              handleSendDeleteAttachmentRequest
            }
            handleResetUploadAttachmentRequest={
              handleResetUploadAttachmentRequest
            }
            handleResetDeleteAttachmentRequest={
              handleResetDeleteAttachmentRequest
            }
            uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
            uploadAttachmentRequestError={uploadAttachmentRequestError}
            uploadedAttachment={uploadedAttachment}
            deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
            deleteAttachmentRequestError={deleteAttachmentRequestError}
            deletedAttachmentId={deletedAttachmentId}
            setAttachments={setAttachments}
            attachments={attachments}
            getEmailAccounts={getEmailAccounts}
            emailAccount={emailAccount}
            resetEmailDraftDataRequestStatus={resetEmailDraftDataRequestStatus}
            resetReplyForwardDraftRequests={resetReplyForwardDraftRequests}
            sendEmailProspectTabStatus={sendEmailProspectTabStatus?.status}
            replyEmailRequestStatus={replyEmailRequestStatus?.status}
            setSelectedRepliedEmailId={setSelectedRepliedEmailId}
            handleImageUploadRequest={handleImageUploadRequest}
            emailRequestStatus={emailRequestStatus}
          />
          <div className="email-divider" />
        </>
      )}

      {!showReplyEditor &&
        !openEditorEmailId &&
        processedEmailsToRender?.length === 0 && (
          <NoProspectDetailResultScreen
            title="No Emails Conversation"
            description="Once you send an email, it will appear here for easy tracking."
            imageSrc={Images.NoProspectEmail}
          />
        )}
      {/* ========================================================================
          EMAIL LIST SECTION
          ======================================================================== */}
      {(processedEmails?.length > 0 || emailWithOpenEditor) && (
        <div
          ref={scrollContainerRef}
          className="email-list-tab-body-main-container"
        >
          {processedEmails?.map((email) => {
            const isDraft = email?.isDraft === true;
            const hasUnifiedScheduledDraft =
              email?.unifiedScheduled &&
              email?.isDraft === false &&
              email?.repliedEmail === true;
            const isHighlighted = highlightedEmailIds.has(email.emailId);
            const isEditorOpenForThisEmail =
              openEditorEmailId === email.emailId;
            // For draft emails, hide the email when editor is open (editor replaces it)
            // For regular emails, show both editor and email (editor above, email below)
            const isDraftEmailBeingEdited =
              isEditorOpenForThisEmail && (isDraft || hasUnifiedScheduledDraft);

            // When editor is open, render editor
            // For drafts: only show editor (replaces the email)
            // For regular emails: show editor above and email below
            if (isEditorOpenForThisEmail) {
              return (
                <>
                  <div key={`editor-and-email-${email.emailId}`}>
                    {/* Inline Editor Above Email */}
                    <div
                      className="email-list-inline-editor-container"
                      data-email-id={email.emailId}
                    >
                      {openEditorType === 'reply' && inlineEditor ? (
                        <ReplyForwardEditor
                          label={getReplyForwardEmailEditor('reply')}
                          action="reply"
                          selectedEmail={convertToReplyForwardEmailPreview(
                            email,
                          )}
                          onHideReplyEditor={handleCancelInlineEditor}
                          hashId={inlineEditor.hashId}
                          emailThreadId={String(
                            inlineEditor.emailThreadId || '',
                          )}
                          showReplyEditor={true}
                          userTimezone={
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                          }
                          unifiedSchedule={
                            inlineReplyUnifiedSchedule ||
                            email?.unifiedScheduled
                          }
                          sequenceId={
                            allEmails.find(
                              (e) => e.emailId === inlineEditor.emailId,
                            )?.sequenceId || 0
                          }
                          uniboxFiltersFeed={UnifiedInboxFeed.All}
                          shouldFetchEmailThreadsOnReplyAndForward={false}
                          showSubject={true}
                          subjectValue={inlineEditor?.subject}
                        />
                      ) : openEditorType === 'forward' && inlineEditor ? (
                        <ReplyForwardEditor
                          label={getReplyForwardEmailEditor('forward')}
                          action="forward"
                          selectedEmail={convertToReplyForwardEmailPreview(
                            email,
                          )}
                          onHideReplyEditor={handleCancelInlineEditor}
                          hashId={inlineEditor.hashId}
                          emailThreadId={String(
                            inlineEditor.emailThreadId || '',
                          )}
                          showReplyEditor={true}
                          userTimezone={
                            Intl.DateTimeFormat().resolvedOptions().timeZone
                          }
                          sequenceId={0}
                          uniboxFiltersFeed={UnifiedInboxFeed.All}
                          shouldFetchEmailThreadsOnReplyAndForward={false}
                        />
                      ) : openEditorType === 'draft' &&
                        defaultDraftEditorFields ? (
                        <CustomReplyForwardEditor
                          key={`editor-draft-${email.emailId}`}
                          label="Edit Draft"
                          showFromField={undefined}
                          editorDefaultFields={draftEditorDefaults}
                          onSend={handleSend}
                          onDiscard={handleCancelInlineEditor}
                          inlineEditorInputValue={inlineEditorInputValue}
                          setInlineEditorInputValue={setInlineEditorInputValue}
                          mergeTagOptions={fields}
                          fromAddresses={filterConnectedEmailAccounts}
                          draftProspectEmailRequest={draftProspectEmailRequest}
                          draftProspectEmailRequestStatus={
                            draftProspectEmailRequestStatus
                          }
                          selectedProspectId={selectedProspectId}
                          sendReplyDraftDiscardRequest={
                            sendReplyDraftDiscardRequest
                          }
                          defaultDraftEditorFields={defaultDraftEditorFields}
                          sequenceId={email.sequenceId}
                          hashId={email.hashId}
                          handleSendAttachmentUploadRequest={
                            handleSendAttachmentUploadRequest
                          }
                          handleSendDeleteAttachmentRequest={
                            handleSendDeleteAttachmentRequest
                          }
                          handleResetUploadAttachmentRequest={
                            handleResetUploadAttachmentRequest
                          }
                          handleResetDeleteAttachmentRequest={
                            handleResetDeleteAttachmentRequest
                          }
                          uploadAttachmentRequestStatus={
                            uploadAttachmentRequestStatus
                          }
                          uploadAttachmentRequestError={
                            uploadAttachmentRequestError
                          }
                          uploadedAttachment={uploadedAttachment}
                          deleteAttachmentRequestStatus={
                            deleteAttachmentRequestStatus
                          }
                          deleteAttachmentRequestError={
                            deleteAttachmentRequestError
                          }
                          deletedAttachmentId={deletedAttachmentId}
                          setAttachments={setAttachments}
                          attachments={attachments}
                          getEmailAccounts={getEmailAccounts}
                          emailAccount={emailAccount}
                          resetEmailDraftDataRequestStatus={
                            resetEmailDraftDataRequestStatus
                          }
                          resetReplyForwardDraftRequests={
                            resetReplyForwardDraftRequests
                          }
                          sendEmailProspectTabStatus={
                            sendEmailProspectTabStatus?.status
                          }
                          replyEmailRequestStatus={
                            replyEmailRequestStatus?.status
                          }
                          setSelectedRepliedEmailId={setSelectedRepliedEmailId}
                          handleImageUploadRequest={handleImageUploadRequest}
                          emailRequestStatus={emailRequestStatus}
                        />
                      ) : null}
                    </div>
                    {/* For draft emails: hide the email when editor is open (editor replaces it) */}
                    {/* For regular emails: show email below the editor */}
                    {!isDraftEmailBeingEdited && (
                      <div
                        key={`${email.emailThreadId}-${email.emailId}`}
                        className={`email-list-single-email-conatiner ${
                          isHighlighted ? 'email-list-tab-highlighted' : ''
                        }`}
                      >
                        {/* Draft Editor Below Email (if unifiedScheduled is not null and opened) */}
                        {/* {hasUnifiedScheduledDraft &&
                  openedUnifiedScheduledDraftEmailId === email.emailId &&
                  (() => {
                    const draftStateValues = parseUnifiedScheduledToStateValues(
                      email.unifiedScheduled,
                      email,
                    );
                    if (!draftStateValues) return null;

                    return (
                      <div className="email-list-draft-editor-container">
                        <CustomReplyForwardEditor
                          label="Draft Email"
                          sequenceId={email.sequenceId}
                          onSend={(data) =>
                            handleInlineDraftSend(
                              draftStateValues.emailId,
                              data,
                              draftStateValues.unifiedScheduledId,
                            )
                          }
                          onDiscard={() => {
                            // Close the inline editor for this email
                            setOpenedUnifiedScheduledDraftEmailId(null);
                          }}
                          inlineEditorInputValue={draftStateValues.subject}
                          setInlineEditorInputValue={() => {
                            // Handle subject change if needed
                          }}
                          mergeTagOptions={fields}
                          fromAddresses={filterConnectedEmailAccounts}
                          draftProspectEmailRequest={draftProspectEmailRequest}
                          draftProspectEmailRequestStatus={
                            draftProspectEmailRequestStatus
                          }
                          selectedProspectId={selectedProspectId}
                          sendReplyDraftDiscardRequest={
                            sendReplyDraftDiscardRequest
                          }
                          defaultDraftEditorFields={draftStateValues}
                          hashId={draftStateValues.hashId}
                          handleSendAttachmentUploadRequest={
                            handleSendAttachmentUploadRequest
                          }
                          handleSendDeleteAttachmentRequest={
                            handleSendDeleteAttachmentRequest
                          }
                          handleResetUploadAttachmentRequest={
                            handleResetUploadAttachmentRequest
                          }
                          handleResetDeleteAttachmentRequest={
                            handleResetDeleteAttachmentRequest
                          }
                          uploadAttachmentRequestStatus={
                            uploadAttachmentRequestStatus
                          }
                          uploadAttachmentRequestError={
                            uploadAttachmentRequestError
                          }
                          uploadedAttachment={uploadedAttachment}
                          deleteAttachmentRequestStatus={
                            deleteAttachmentRequestStatus
                          }
                          deleteAttachmentRequestError={
                            deleteAttachmentRequestError
                          }
                          deletedAttachmentId={deletedAttachmentId}
                          setAttachments={(newAttachments) => {
                            setInlineDraftAttachments((prev) => ({
                              ...prev,
                              [email.emailId]:
                                typeof newAttachments === 'function'
                                  ? newAttachments(prev[email.emailId] || [])
                                  : newAttachments,
                            }));
                          }}
                          attachments={
                            inlineDraftAttachments[email.emailId] || []
                          }
                          getEmailAccounts={getEmailAccounts}
                          emailAccount={emailAccount}
                          resetEmailDraftDataRequestStatus={
                            resetEmailDraftDataRequestStatus
                          }
                          resetReplyForwardDraftRequests={
                            resetReplyForwardDraftRequests
                          }
                          sendEmailProspectTabStatus={
                            sendEmailProspectTabStatus?.status
                          }
                          setSelectedRepliedEmailId={setSelectedRepliedEmailId}
                          editorDefaultFields={draftEditorDefaults}
                          handleImageUploadRequest={handleImageUploadRequest}
                        />
                      </div>
                    );
                  })()} */}
                        {/* Email Header */}
                        <div className="email-list-single-email-header">
                          <div className="email-list-single-email-header-subject-icon">
                            <span className="email-list-single-email-icon">
                              {email.fromProspectId ? (
                                <EmailComposed />
                              ) : email?.isDraft ||
                                (email?.unifiedScheduled &&
                                  email?.repliedEmail === true) ? (
                                <EmailDraft />
                              ) : (
                                <EmailSent />
                              )}
                            </span>
                            <div className="email-list-single-email-subject">
                              <div>
                                <TruncatedTextWithAutoTooltip
                                  text={email?.subject}
                                />
                              </div>
                              {email?.sequenceName ? (
                                <div
                                  className="email-list-single-email-sequence-name"
                                  {...accessibleOnClick(() => {
                                    if (email?.sequenceId) {
                                      redirectWithoutRefresh(
                                        `/sequence/${email?.sequenceId}/steps`,
                                      );
                                    }
                                  })}
                                >
                                  <Send
                                    width={14}
                                    height={14}
                                    className="email-list-send-email-icon"
                                  />

                                  <TruncatedTextWithAutoTooltip
                                    text={email?.sequenceName}
                                  />
                                </div>
                              ) : (
                                <div className="email-list-single-email-sequence-name">
                                  <ExternalEmailSent
                                    width={14}
                                    height={14}
                                    // className="email-list-send-email-icon"
                                  />
                                  <span className="email-list-single-email-external-email">
                                    External
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="email-list-single-email-header-detail">
                            <div className="email-list-single-email-date">
                              <span>
                                {formateDateWithTime(
                                  getFormattedDate(
                                    email?.sentAt ??
                                      email?.unifiedScheduled?.createdAt,
                                    userTimezone,
                                  ),
                                )}
                              </span>
                              {/* Email Last Openend At Icon */}
                              {email?.lastOpenedAt && (
                                <OverlayTooltip
                                  text={openedAtTooltipText(email)}
                                >
                                  <span
                                    className={`email-list-icon-action ${
                                      !email?.lastOpenedAt
                                        ? 'cursor-not-allowed'
                                        : ''
                                    }`}
                                    {...accessibleOnClick(() =>
                                      handleActivityModalOpen(
                                        MasterLog.UniboxEmailOpened,
                                        email,
                                      ),
                                    )}
                                  >
                                    <Eye
                                      width={16}
                                      height={16}
                                      className={
                                        !email?.lastOpenedAt
                                          ? 'cursor-not-allowed'
                                          : 'cursor-pointer'
                                      }
                                    />
                                  </span>
                                </OverlayTooltip>
                              )}
                              {/* Email Last Clicked At Icon */}
                              {email?.lastClickedAt && (
                                <OverlayTooltip
                                  text={clickedAtTooltipText(email)}
                                >
                                  <span
                                    className={`email-list-icon-action ${
                                      !email?.lastClickedAt
                                        ? 'cursor-not-allowed'
                                        : ''
                                    }`}
                                    {...accessibleOnClick(() =>
                                      handleActivityModalOpen(
                                        MasterLog.UniboxEmailLinkClicked,
                                        email,
                                      ),
                                    )}
                                  >
                                    <Click
                                      width={16}
                                      height={16}
                                      className={
                                        !email?.lastClickedAt
                                          ? 'cursor-not-allowed'
                                          : 'cursor-pointer'
                                      }
                                    />
                                  </span>
                                </OverlayTooltip>
                              )}
                              {isDraft || hasUnifiedScheduledDraft ? (
                                <OverlayTooltip text="Edit Draft">
                                  <span
                                    className="email-list-icon-action"
                                    {...accessibleOnClick(() => {
                                      if (hasUnifiedScheduledDraft) {
                                        handleEditUnifiedScheduledDraftClick(
                                          email,
                                        );
                                      } else {
                                        handleEditDraftClick(email);
                                      }
                                    })}
                                  >
                                    <Pencil width={16} height={16} />
                                  </span>
                                </OverlayTooltip>
                              ) : (
                                <>
                                  <OverlayTooltip text="Reply">
                                    <span
                                      className="email-list-icon-action"
                                      {...accessibleOnClick(() =>
                                        handleReplyClick(email),
                                      )}
                                    >
                                      <ArrowBackUp width={16} height={16} />
                                    </span>
                                  </OverlayTooltip>
                                  <OverlayTooltip text="Forward">
                                    <span
                                      className="email-list-icon-action"
                                      {...accessibleOnClick(() =>
                                        handleForwardClick(email),
                                      )}
                                    >
                                      <ArrowForwardUp width={16} height={16} />
                                    </span>
                                  </OverlayTooltip>
                                </>
                              )}
                              <OverlayTooltip text="Delete">
                                <Button
                                  className="email-list-icon-action"
                                  {...accessibleOnClick(() =>
                                    setDeleteEmailId(email.emailId),
                                  )}
                                  disabled={!!email?.sequenceId}
                                >
                                  <Trash width={16} height={16} />
                                </Button>
                              </OverlayTooltip>
                            </div>
                          </div>
                        </div>

                        {/* Email Body */}
                        <div
                          ref={(el) => {
                            emailContentRefs.current[email.emailId] = el;
                          }}
                          className={`email-list-single-email-body ${
                            isDraft || hasUnifiedScheduledDraft
                              ? 'email-list-single-email-body-draft'
                              : ''
                          }`}
                        >
                          <div className="email-list-header-user-avatar">
                            <span>{renderAvatar(email.fromName)}</span>
                            <div className="email-list-user-name-container">
                              <div className="email-list-user-name">
                                {/* <span>{email.fromName}</span>
                          <span>{`<${email.fromEmail}>`}</span> */}
                                <TruncatedTextWithTooltip
                                  string={email.fromName}
                                  length={20}
                                />
                                <TruncatedTextWithTooltip
                                  string={`<${email.fromEmail}>`}
                                  length={40}
                                />
                              </div>
                              <div className="email-list-to-field">
                                {email.to && (
                                  <span>
                                    To: {formatArrayWithCount(email.to)}
                                  </span>
                                )}
                                {!hasUnifiedScheduledDraft && (
                                  <OverlayPopover
                                    show={openPopoverEmailId === email?.emailId}
                                    onToggle={(isOpen) => {
                                      setOpenPopoverEmailId(
                                        isOpen ? email?.emailId : null,
                                      );
                                    }}
                                    rootClose={true}
                                    placement={Placement?.BottomStart}
                                    className="email-list-prospect-detail-popover"
                                    content={
                                      <div className="prospect-detail-modal">
                                        <div className="prospect-detail-single-field-container">
                                          <span className="prospect-detail-single-field-header">
                                            from:
                                          </span>
                                          <span className="prospect-detail-single-field-value">
                                            <span className="prospect-detail-prospect-name">
                                              {email?.fromName}
                                            </span>
                                            {`<${email?.fromEmail}>`}
                                          </span>
                                        </div>

                                        <div className="prospect-detail-single-field-container">
                                          <span className="prospect-detail-single-field-header">
                                            to:{' '}
                                          </span>
                                          <span className="prospect-detail-single-field-value">
                                            {Array.isArray(email?.to)
                                              ? email.to.join(', ')
                                              : email?.to || ''}
                                          </span>
                                        </div>
                                        {email?.cc?.length > 0 && (
                                          <div className="prospect-detail-single-field-container">
                                            <span className="prospect-detail-single-field-header">
                                              cc:
                                            </span>
                                            <span className="prospect-detail-single-field-value">
                                              {Array.isArray(email?.cc)
                                                ? email.cc.join(', ')
                                                : email?.cc || ''}
                                            </span>
                                          </div>
                                        )}

                                        {email?.bcc?.length > 0 && (
                                          <div className="prospect-detail-single-field-container">
                                            <span className="prospect-detail-single-field-header">
                                              bcc:
                                            </span>
                                            <span className="prospect-detail-single-field-value">
                                              {Array.isArray(email?.bcc)
                                                ? email.bcc.join(', ')
                                                : email?.bcc || ''}
                                            </span>
                                          </div>
                                        )}

                                        <div className="prospect-detail-single-field-container">
                                          <span className="prospect-detail-single-field-header">
                                            date:
                                          </span>
                                          <span className="prospect-detail-single-field-value">
                                            {formateDateWithTime(email?.sentAt)}
                                          </span>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <div
                                      className="show-info-popover"
                                      {...accessibleOnClick(() => {
                                        if (
                                          openPopoverEmailId !== email.emailId
                                        ) {
                                          setOpenPopoverEmailId(email.emailId);
                                        }
                                      })}
                                    >
                                      <InfoCircle width={14} height={14} />
                                      <span>Details</span>
                                    </div>
                                  </OverlayPopover>
                                )}
                              </div>
                            </div>
                          </div>
                          <EmailPreviewContent
                            content={
                              email?.isDraft === true
                                ? email?.unifiedScheduled?.content
                                : email?.content
                            }
                            attachments={email?.attachments}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="email-divider" /> */}
                </>
              );
            }

            // Render email normally (editor is not open for it)
            return (
              <div
                key={`${email.emailThreadId}-${email.emailId}`}
                className={`email-list-single-email-conatiner ${
                  isHighlighted ? 'email-list-tab-highlighted' : ''
                }`}
              >
                {/* Draft Editor Below Email (if unifiedScheduled is not null and opened) */}
                {/* {hasUnifiedScheduledDraft &&
                  openedUnifiedScheduledDraftEmailId === email.emailId &&
                  (() => {
                    const draftStateValues = parseUnifiedScheduledToStateValues(
                      email.unifiedScheduled,
                      email,
                    );
                    if (!draftStateValues) return null;

                    return (
                      <div className="email-list-draft-editor-container">
                        <CustomReplyForwardEditor
                          label="Draft Email"
                          sequenceId={email.sequenceId}
                          onSend={(data) =>
                            handleInlineDraftSend(
                              draftStateValues.emailId,
                              data,
                              draftStateValues.unifiedScheduledId,
                            )
                          }
                          onDiscard={() => {
                            // Close the inline editor for this email
                            setOpenedUnifiedScheduledDraftEmailId(null);
                          }}
                          inlineEditorInputValue={draftStateValues.subject}
                          setInlineEditorInputValue={() => {
                            // Handle subject change if needed
                          }}
                          mergeTagOptions={fields}
                          fromAddresses={filterConnectedEmailAccounts}
                          draftProspectEmailRequest={draftProspectEmailRequest}
                          draftProspectEmailRequestStatus={
                            draftProspectEmailRequestStatus
                          }
                          selectedProspectId={selectedProspectId}
                          sendReplyDraftDiscardRequest={
                            sendReplyDraftDiscardRequest
                          }
                          defaultDraftEditorFields={draftStateValues}
                          hashId={draftStateValues.hashId}
                          handleSendAttachmentUploadRequest={
                            handleSendAttachmentUploadRequest
                          }
                          handleSendDeleteAttachmentRequest={
                            handleSendDeleteAttachmentRequest
                          }
                          handleResetUploadAttachmentRequest={
                            handleResetUploadAttachmentRequest
                          }
                          handleResetDeleteAttachmentRequest={
                            handleResetDeleteAttachmentRequest
                          }
                          uploadAttachmentRequestStatus={
                            uploadAttachmentRequestStatus
                          }
                          uploadAttachmentRequestError={
                            uploadAttachmentRequestError
                          }
                          uploadedAttachment={uploadedAttachment}
                          deleteAttachmentRequestStatus={
                            deleteAttachmentRequestStatus
                          }
                          deleteAttachmentRequestError={
                            deleteAttachmentRequestError
                          }
                          deletedAttachmentId={deletedAttachmentId}
                          setAttachments={(newAttachments) => {
                            setInlineDraftAttachments((prev) => ({
                              ...prev,
                              [email.emailId]:
                                typeof newAttachments === 'function'
                                  ? newAttachments(prev[email.emailId] || [])
                                  : newAttachments,
                            }));
                          }}
                          attachments={
                            inlineDraftAttachments[email.emailId] || []
                          }
                          getEmailAccounts={getEmailAccounts}
                          emailAccount={emailAccount}
                          resetEmailDraftDataRequestStatus={
                            resetEmailDraftDataRequestStatus
                          }
                          resetReplyForwardDraftRequests={
                            resetReplyForwardDraftRequests
                          }
                          sendEmailProspectTabStatus={
                            sendEmailProspectTabStatus?.status
                          }
                          setSelectedRepliedEmailId={setSelectedRepliedEmailId}
                          editorDefaultFields={draftEditorDefaults}
                          handleImageUploadRequest={handleImageUploadRequest}
                        />
                      </div>
                    );
                  })()} */}
                {/* Email Header */}
                <div className="email-list-single-email-header">
                  <div className="email-list-single-email-header-subject-icon">
                    <span className="email-list-single-email-icon">
                      {email?.fromProspectId && !email?.repliedEmail ? (
                        <EmailComposed />
                      ) : email?.isDraft ||
                        (email?.unifiedScheduled &&
                          email?.repliedEmail === true) ? (
                        <EmailDraft />
                      ) : (
                        <EmailSent />
                      )}
                    </span>
                    <div className="email-list-single-email-subject">
                      <div>
                        <TruncatedTextWithAutoTooltip text={email?.subject} />
                      </div>
                      {email?.sequenceName ? (
                        <div
                          className="email-list-single-email-sequence-name"
                          {...accessibleOnClick(() => {
                            if (email?.sequenceId) {
                              redirectWithoutRefresh(
                                `/sequence/${email?.sequenceId}/steps`,
                              );
                            }
                          })}
                        >
                          <Send
                            width={14}
                            height={14}
                            className="email-list-send-email-icon"
                          />

                          <TruncatedTextWithAutoTooltip
                            text={email?.sequenceName}
                          />
                        </div>
                      ) : (
                        <div className="email-list-single-email-sequence-name">
                          <ExternalEmailSent
                            width={14}
                            height={14}
                            // className="email-list-send-email-icon"
                          />
                          <span className="email-list-single-email-external-email">
                            External
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="email-list-single-email-header-detail">
                    <div className="email-list-single-email-date">
                      <span>
                        {formateDateWithTime(
                          getFormattedDate(
                            email?.sentAt ?? email?.unifiedScheduled?.createdAt,
                            userTimezone,
                          ),
                        )}
                      </span>
                      {/* Email Last Openend At Icon */}
                      {email?.lastOpenedAt && (
                        <OverlayTooltip text={openedAtTooltipText(email)}>
                          <span
                            className={`email-list-icon-action ${
                              !email?.lastOpenedAt ? 'cursor-not-allowed' : ''
                            }`}
                            {...accessibleOnClick(() =>
                              handleActivityModalOpen(
                                MasterLog.UniboxEmailOpened,
                                email,
                              ),
                            )}
                          >
                            <Eye
                              width={16}
                              height={16}
                              className={
                                !email?.lastOpenedAt
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }
                            />
                          </span>
                        </OverlayTooltip>
                      )}
                      {/* Email Last Clicked At Icon */}
                      {email?.lastClickedAt && (
                        <OverlayTooltip text={clickedAtTooltipText(email)}>
                          <span
                            className={`email-list-icon-action ${
                              !email?.lastClickedAt ? 'cursor-not-allowed' : ''
                            }`}
                            {...accessibleOnClick(() =>
                              handleActivityModalOpen(
                                MasterLog.UniboxEmailLinkClicked,
                                email,
                              ),
                            )}
                          >
                            <Click
                              width={16}
                              height={16}
                              className={
                                !email?.lastClickedAt
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }
                            />
                          </span>
                        </OverlayTooltip>
                      )}
                      {isDraft || hasUnifiedScheduledDraft ? (
                        <OverlayTooltip text="Edit Draft">
                          <span
                            className="email-list-icon-action"
                            {...accessibleOnClick(() => {
                              if (hasUnifiedScheduledDraft) {
                                handleEditUnifiedScheduledDraftClick(email);
                              } else {
                                handleEditDraftClick(email);
                              }
                            })}
                          >
                            <Pencil width={16} height={16} />
                          </span>
                        </OverlayTooltip>
                      ) : (
                        <>
                          <OverlayTooltip text="Reply">
                            <span
                              className="email-list-icon-action"
                              {...accessibleOnClick(() =>
                                handleReplyClick(email),
                              )}
                            >
                              <ArrowBackUp width={16} height={16} />
                            </span>
                          </OverlayTooltip>
                          <OverlayTooltip text="Forward">
                            <span
                              className="email-list-icon-action"
                              {...accessibleOnClick(() =>
                                handleForwardClick(email),
                              )}
                            >
                              <ArrowForwardUp width={16} height={16} />
                            </span>
                          </OverlayTooltip>
                        </>
                      )}
                      <OverlayTooltip text="Delete">
                        <Button
                          className="email-list-icon-action"
                          {...accessibleOnClick(() =>
                            setDeleteEmailId(email.emailId),
                          )}
                          disabled={!!email?.sequenceId}
                        >
                          <Trash width={16} height={16} />
                        </Button>
                      </OverlayTooltip>
                    </div>
                  </div>
                </div>

                {/* Email Body */}
                <div
                  ref={(el) => {
                    emailContentRefs.current[email.emailId] = el;
                  }}
                  className={`email-list-single-email-body ${
                    isDraft || hasUnifiedScheduledDraft
                      ? 'email-list-single-email-body-draft'
                      : ''
                  }`}
                >
                  <div className="email-list-header-user-avatar">
                    <span>{renderAvatar(email.fromName)}</span>
                    <div className="email-list-user-name-container">
                      <div className="email-list-user-name">
                        {/* <span>{email.fromName}</span>
                          <span>{`<${email.fromEmail}>`}</span> */}
                        <TruncatedTextWithTooltip
                          string={email.fromName}
                          length={20}
                        />
                        <TruncatedTextWithTooltip
                          string={`<${email.fromEmail}>`}
                          length={40}
                        />
                      </div>
                      <div className="email-list-to-field">
                        {email.to && (
                          <span>To: {formatArrayWithCount(email.to)}</span>
                        )}
                        {!hasUnifiedScheduledDraft && (
                          <OverlayPopover
                            show={openPopoverEmailId === email?.emailId}
                            onToggle={(isOpen) => {
                              setOpenPopoverEmailId(
                                isOpen ? email?.emailId : null,
                              );
                            }}
                            rootClose={true}
                            placement={Placement?.BottomStart}
                            className="email-list-prospect-detail-popover"
                            content={
                              <div className="prospect-detail-modal">
                                <div className="prospect-detail-single-field-container">
                                  <span className="prospect-detail-single-field-header">
                                    from:
                                  </span>
                                  <span className="prospect-detail-single-field-value">
                                    <span className="prospect-detail-prospect-name">
                                      {email?.fromName}
                                    </span>
                                    {`<${email?.fromEmail}>`}
                                  </span>
                                </div>

                                <div className="prospect-detail-single-field-container">
                                  <span className="prospect-detail-single-field-header">
                                    to:{' '}
                                  </span>
                                  <span className="prospect-detail-single-field-value">
                                    {Array.isArray(email?.to)
                                      ? email.to.join(', ')
                                      : email?.to || ''}
                                  </span>
                                </div>
                                {email?.cc?.length > 0 && (
                                  <div className="prospect-detail-single-field-container">
                                    <span className="prospect-detail-single-field-header">
                                      cc:
                                    </span>
                                    <span className="prospect-detail-single-field-value">
                                      {Array.isArray(email?.cc)
                                        ? email.cc.join(', ')
                                        : email?.cc || ''}
                                    </span>
                                  </div>
                                )}

                                {email?.bcc?.length > 0 && (
                                  <div className="prospect-detail-single-field-container">
                                    <span className="prospect-detail-single-field-header">
                                      bcc:
                                    </span>
                                    <span className="prospect-detail-single-field-value">
                                      {Array.isArray(email?.bcc)
                                        ? email.bcc.join(', ')
                                        : email?.bcc || ''}
                                    </span>
                                  </div>
                                )}

                                <div className="prospect-detail-single-field-container">
                                  <span className="prospect-detail-single-field-header">
                                    date:
                                  </span>
                                  <span className="prospect-detail-single-field-value">
                                    {formateDateWithTime(email?.sentAt)}
                                  </span>
                                </div>
                              </div>
                            }
                          >
                            <div
                              className="show-info-popover"
                              {...accessibleOnClick(() => {
                                if (openPopoverEmailId !== email.emailId) {
                                  setOpenPopoverEmailId(email.emailId);
                                }
                              })}
                            >
                              <InfoCircle width={14} height={14} />
                              <span>Details</span>
                            </div>
                          </OverlayPopover>
                        )}
                      </div>
                    </div>
                  </div>
                  <EmailPreviewContent
                    content={
                      email?.isDraft === true
                        ? email?.unifiedScheduled?.content
                        : email?.content
                    }
                    attachments={email?.attachments}
                  />
                </div>
              </div>
            );
          })}
          {isLoadingMore && (
            <div className="email-list-loading d-flex justify-content-center">
              <Spinner className="spinner--blue" animation="border" />
            </div>
          )}
          {hasMoreEmails === false && emailsToRender?.length > pageLimit && (
            <div className="end-list-divider">
              <span>The End</span>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================
          DELETE CONFIRMATION MODAL
          ======================================================================== */}
      {deleteEmailId !== null && (
        <ConfirmationModal
          show={true}
          showCheckbox={true}
          onSubmit={(isChecked) =>
            handleDeleteEmail(Math.abs(Number(deleteEmailId)), isChecked)
          }
          onClose={() => setDeleteEmailId(null)}
          CustomContent="This email will be removed from Saleshandy, but it will remain in your email inbox."
          customHeaderText="Are you sure you want to delete this email ?"
          customClassName="email-list-trash-icon-confirmation-modal"
          submitButtonClassName="email-list-trash-modal-submit-btn"
          hideHeader
          CustomHeaderIcon={<Trash className="email-list-trash-icon" />}
        />
      )}

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Sending email is disabled in demo mode"
        content="This is a demo account with sample data. Signup to send emails"
        primaryBtnLabel="Sign up"
      />

      {/* ========================================================================
          ACTIVITY MODAL
          ======================================================================== */}
      {activityModalEmail && (
        <UnifiedInboxEmailsActivityModal
          show={showUnifiedInboActivityModal}
          tab={unifiedInboxEmailsActivityTab}
          onClose={handleActivityModalClose}
          userTimezone={timeZone}
          subject={activityModalEmail.subject}
          emailId={activityModalEmail.emailId}
          email={activityModalEmail.email}
          unifiedInboxEmailThreadActivity={unifiedInboxEmailThreadActivity}
          sendGetEmailThreadActivity={sendGetEmailThreadActivity}
          resetUnifiedInboxEmailThreadActivity={
            resetUnifiedInboxEmailThreadActivity
          }
          getEmailThreadActivityRequestStatus={
            getEmailThreadActivityRequestStatus
          }
          getEmailThreadActivityRequestError={
            getEmailThreadActivityRequestError
          }
          className="prospect-kanban-email-thread-activity-modal"
        />
      )}
    </div>
  );
};

export default EmailListTab;
